import { PaymentDto, SelectListItemDto } from '@/app/models/checkout/types';
import React, { useState, forwardRef, useImperativeHandle } from 'react';

export interface RewardPointsHandle {
    getValue: () => number | null;
    validate: () => boolean;
    setValue: (value: number | string) => void;
}

interface RewardPointsProps {
    useRewardPoints: boolean;
    payment: PaymentDto;
    selectedPaymentMethod: string;
    onRewardPointsChange?: (value: number | string) => void;
    onValidityChange?: (isInvalid: boolean) => void;
}

const RewardPointsSection = forwardRef<RewardPointsHandle, RewardPointsProps>(
    ({ useRewardPoints, payment, selectedPaymentMethod, onRewardPointsChange, onValidityChange }, ref) => {
        const [rewardPointsInput, setRewardPointsInput] = useState<number | string>('');
        const [isInvalid, setIsInvalid] = useState(false);
        const validateRewardPoints = (raw: string | number | null | undefined): boolean => {
            if (raw === '' || raw === null || raw === undefined) {
                setIsInvalid(false);
                onValidityChange?.(false);
                return true;
            }

            const num = Number(raw);
            const invalid =
                isNaN(num) ||
                num <= 0 ||
                num > payment.rewardPointsBalance ||
                num > payment.maxApplicableRewardPoints;

            setIsInvalid(invalid);
            onValidityChange?.(invalid);
            return !invalid;
        };

        useImperativeHandle(ref, () => ({
            getValue: () => {
                if (rewardPointsInput === '' || rewardPointsInput === null || rewardPointsInput === undefined) return null;
                return validateRewardPoints(rewardPointsInput) ? Number(rewardPointsInput) : null;
            },
            validate: () => validateRewardPoints(rewardPointsInput),
            setValue: (v: number | string) => {
                setRewardPointsInput(v);
                onRewardPointsChange?.(v);
            },
        }));

        return (
            <div className="checkout-data">
                <div className="section promotions" style={{ marginTop: 0 }}>
                    <input id="max-promotion" type="hidden" value={payment.maxApplicableRewardPoints} />
                    {payment.displayRewardPoints && (
                        <>
                            {useRewardPoints ? (
                                <>
                                    <span style={{ display: 'block', color: '#767676', fontSize: '14px', marginBottom: '4px', fontWeight: 500 }}>
                                        Impact Rewards
                                    </span>
                                    <input
                                        id="opc-promotions"
                                        type="text"
                                        inputMode="numeric"
                                        step="any"
                                        min="0"
                                        pattern="\d*"
                                        max={payment.maxApplicableRewardPoints}
                                        name="SelectedRewardPoints"
                                        value={rewardPointsInput}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (/^\d*$/.test(value)) {
                                                setRewardPointsInput(value);
                                            }
                                        }}
                                        onBlur={(e) => validateRewardPoints(e.target.value)}
                                        style={{
                                            width: '150px',
                                            padding: '4px 6px',
                                            border: isInvalid ? '2px solid red' : '1px solid #ccc',
                                            borderRadius: '4px',
                                            color: isInvalid ? 'red' : 'inherit',
                                            outline: 'none',
                                        }}
                                    />
                                </>
                            ) : (
                                <div className="use-reward-points">
                                    <select
                                        id="opc-promotions"
                                        name="SelectedRewardPoints"
                                        className="impact-rewards-input"
                                        value={rewardPointsInput}
                                        onChange={(e) => {
                                            setRewardPointsInput(e.target.value);
                                        }}
                                        onBlur={() => validateRewardPoints(rewardPointsInput)}
                                        style={{
                                            width: '150px',
                                            padding: '4px 6px',
                                            border: isInvalid ? '2px solid red' : '1px solid #ccc',
                                            borderRadius: '4px',
                                            color: isInvalid ? 'red' : 'inherit',
                                            outline: 'none',
                                        }}
                                    >
                                        {payment.availableRewardPoints.map((option: SelectListItemDto) => (
                                            <option key={option.value} value={option.value}>
                                                {option.text}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <p style={{ marginTop: '10px', fontSize: '14px' }} className="probucks-msg">
                                {payment.orderGreaterThan50 ? (
                                    <>
                                        <b>{payment.maxApplicableRewardPointsAmount}</b> in Impact Rewards are available for use on this order
                                        from your balance of <b>${payment.rewardPointsBalance.toFixed(2)}</b>. Impact Rewards used cannot exceed <b>50%</b> of
                                        your order subtotal.
                                    </>
                                ) : (
                                    <>Impact Rewards cannot be used on orders less than <b>$50.00</b>.</>
                                )}
                            </p>
                        </>
                    )}

                    <input type="hidden" name="SelectedPaymentMethod" value={selectedPaymentMethod} />
                </div>
            </div>
        );
    }
);

RewardPointsSection.displayName = 'RewardPointsSection';
export default RewardPointsSection;
